import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Eye, Globe, Loader2 } from 'lucide-react';
import api from '../../utils/api';
import LandingPageV3 from '../landing-v3/LandingPageV3';
// PB-I18N-02 — row copy may be a legacy plain string or the multilingual map
// {en, tr, ...}. Rendering the map directly would crash with "Objects are not
// valid as a React child" and would put [object Object] in the alt text.
import { getLocalizedField } from '../../context/LanguageContext';

const ROWS_KEY = 'provider_benefits_rows';

/**
 * V3DraftReview — one authenticated place to review the complete V3 candidate.
 *
 * Reuses the existing CMS preview mechanism: the page below is the real
 * LandingPageV3, fed by `GET /admin/landing-content/effective-preview`
 * (`include_drafts=true`), and the provider-benefit band is the STAGED,
 * unpublished site-content version read through the existing admin version
 * endpoint. Nothing is approved, published or activated here, and every image
 * is fetched with the reviewer's own session — the public approval filter is
 * untouched and anonymous visitors never reach this route.
 */
const V3DraftReview = ({ embedded = false }) => {
  const [surface, setSurface] = useState('GLOBAL');
  const [rows, setRows] = useState(null);
  const [rowsMeta, setRowsMeta] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data: hist } = await api.get(`/admin/site-content/${ROWS_KEY}/history?limit=20`);
        const staged = (hist?.versions || [])
          .filter((v) => !v.is_active)
          .sort((a, b) => (b.version || 0) - (a.version || 0))[0];
        if (!staged) return;
        const { data: ver } = await api.get(
          `/admin/site-content/${ROWS_KEY}/version/${staged.version}`);
        if (!alive) return;
        setRows(ver?.content?.payload?.blocks || []);
        setRowsMeta({ version: staged.version, is_active: staged.is_active });
      } catch {
        if (alive) setRows([]);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const imageFor = (row) => (surface === 'TR' ? row?.image_url_tr : row?.image_url);

  return (
    <div className={embedded ? '' : 'min-h-screen bg-slate-100'} data-testid="v3-draft-review">
      <header className={`border-b border-slate-200 px-4 py-3 ${
        embedded ? 'rounded-t-lg bg-white' : 'sticky top-0 z-50 bg-white/95 backdrop-blur'}`}>
        <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-3">
          {!embedded && (
            <Link to="/treatx-admin/landing-cms"
                  className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900"
                  data-testid="v3-draft-review-back">
              <ArrowLeft className="h-4 w-4" /> Landing CMS
            </Link>
          )}
          <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
            <Eye className="h-4 w-4 text-teal-600" /> V3 candidate — draft review
          </div>
          <div className="ml-auto flex items-center gap-1 rounded-full bg-slate-100 p-1">
            {['GLOBAL', 'TR'].map((s) => (
              <button key={s} type="button" onClick={() => setSurface(s)}
                      className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold transition-colors ${
                        surface === s ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                      data-testid={`v3-draft-review-surface-${s.toLowerCase()}`}>
                <Globe className="h-3 w-3" /> {s}
              </button>
            ))}
          </div>
        </div>
        <p className="mx-auto mt-2 max-w-7xl text-xs text-slate-600">
          Drafts are shown with your session only. Nothing on this screen is published:
          V1 stays live, the provider band is a staged version, and each visual still
          needs your approval in Product Visuals.
        </p>
      </header>

      <section className="border-b border-slate-200 bg-white" data-testid="v3-draft-review-page">
        <LandingPageV3 draftPreview previewSurface={surface} />
      </section>

      <section className="mx-auto max-w-7xl px-4 py-10" data-testid="v3-draft-review-provider">
        <h2 className="text-base font-semibold text-slate-900 md:text-lg">
          Provider benefits — staged visuals
          {rowsMeta && (
            <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-800"
                  data-testid="v3-draft-review-rows-status">
              version {rowsMeta.version} · not published
            </span>
          )}
        </h2>
        <p className="mt-1 text-sm text-slate-600">
          These are the candidate images for the provider page, shown for the {surface} surface.
        </p>
        {loading && (
          <div className="mt-6 flex items-center gap-2 text-sm text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading staged visuals…
          </div>
        )}
        <div className="mt-6 grid gap-6 lg:grid-cols-2">
          {(rows || []).filter((r) => imageFor(r)).map((row, i) => {
            const headline = getLocalizedField(row.headline, 'en');
            const body = getLocalizedField(row.body, 'en');
            return (
              <figure key={headline || i} className="overflow-hidden rounded-xl border border-slate-200 bg-white"
                      data-testid={`v3-draft-review-row-${i}`}>
                <img src={imageFor(row)} alt={headline || `Provider benefit ${i + 1}`}
                     className="w-full object-contain" />
                <figcaption className="border-t border-slate-100 px-4 py-3">
                  <p className="text-sm font-medium text-slate-900">{headline || '—'}</p>
                  {body && <p className="mt-1 text-xs text-slate-600">{body}</p>}
                </figcaption>
              </figure>
            );
          })}
        </div>
        {!loading && !(rows || []).some((r) => imageFor(r)) && (
          <p className="mt-6 text-sm text-slate-500" data-testid="v3-draft-review-rows-empty">
            No staged provider visual carries an image for this surface.
          </p>
        )}
      </section>
    </div>
  );
};

export default V3DraftReview;
